import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch } from "react-redux";
import { useNavigate } from 'react-router-dom';
import { listDevice, printHistory } from '../_actions/user_action';

import '../css/Header.css';
import '../css/Body.css';

import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { Pagination } from '@mui/material';

import DatePicker from "react-datepicker";
import 'react-datepicker/dist/react-datepicker.css';

import { CSVLink } from 'react-csv';
import moment from 'moment';
import 'moment/locale/ko';
import { getUserAccount, getIsLogin, clearSession } from '../utils/session';

// import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
// import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

function HomePrintList() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const userAccount = getUserAccount() || {};
    const isLogin = getIsLogin();

    const onLogoutHandler = (event) => {
        event.preventDefault();
        clearSession();
        window.location.replace('/');
    };

    const [PartNo, setPartNo] = useState('');
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const dataCount = 10;
    const [deviceListInfo, setDeviceListInfo] = useState([]);
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const arrayPostsRef = useRef([]);

    const LAST_PAGE = Math.ceil(posts.length / dataCount);

    useEffect(() => {
        // 마운트 확인용 (운영자 F12 콘솔에서 즉시 확인). 상태 안정화 후 제거 예정.
        // eslint-disable-next-line no-console
        console.log('[HomePrintList] mounted. isLogin=', isLogin, 'userAccount=', !!userAccount && !!userAccount.email);
        // isLogin 은 boolean 이다 (session.js getIsLogin). 이전의 문자열 비교 `!== "OK"` 는
        // 항상 참이 되어 무조건 /Login 으로 튕겼고, Login 의 useEffect 가 즉시 /Home 으로 되돌려
        // 주소창이 /Home 으로 끝나는 회귀를 만들었다.
        if (!isLogin) {
            navigate('/Login');
            return;
        }

        const body = { id: userAccount.email };
        dispatch(listDevice(body)).then(response => {
            if (response.payload.listSuccess) {
                const filteredDevices = response.payload.deviceList.filter(device => device.sensorId);
                setDeviceListInfo(filteredDevices);
            }
        });
    }, []);

    const formatDateString = (dateString) => {
        return dateString.replace("T", " [").replace("+09:00", "]");
    };

    // 1) 선택값만 저장
    const onChangeHandler = (e) => {
        setPage(1);
        setPartNo(e.target.value);
    };

    // 2) 버튼 클릭 시 실제 조회
    const handleSearch = async () => {
        // validation
        if (!PartNo) {
            alert("장비를 선택하세요.");
            return;
        }
        if (startDate > endDate) {
            alert("시작일이 종료일보다 이후일 수 없습니다.");
            return;
        }

        setLoading(true);
        setPosts([]);

        // devices 배열 구성
        let deviceArray = [];
        if (PartNo === 'ALL') {
            deviceArray = deviceListInfo.map(d => d.sensorId);
        } else {
            const target = deviceListInfo.find(d => d.sensorNumber === PartNo);
            if (target) deviceArray.push(target.sensorId);
        }

        const body = {
            devices: deviceArray,
            startDate: moment(startDate).format("YYYY-MM-DD"),
            endDate: moment(endDate).format("YYYY-MM-DD")
        };

        try {
            const response = await dispatch(printHistory(body));
            if (response.payload.findSuccess) {
                const sensorList = response.payload.sensorList;
                const newPosts = sensorList.map((item, idx) => {
                    const displayDate = formatDateString(item.KSTTime);
                    const sensorNumber = deviceListInfo.find(d => d.sensorId === item.DeviceId)
                        ?.sensorNumber || item.DeviceId;
                    return {
                        pID: idx,
                        pTime: displayDate,
                        pTimestamp: item.Timestamp,
                        pDevice: sensorNumber,
                        pNH3: item.NH3,
                        pH2S: item.H2S,
                        pTemperature: item.Temperature,
                        pHumidity: item.Humidity,
                        pRssi: item.Rssi,
                        pSnr: item.Snr
                    };
                });
                arrayPostsRef.current = newPosts;
                setPosts(newPosts);
                setPage(1);
            } else {
                // alert('선택한 장비의 이력이 없습니다.');
            }
        } catch (err) {
            console.error(err);
            alert('이력 조회 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const onPageHandler = (e, value) => {
        setPage(value);
    };

    const headers = [
        { label: "측정시간", key: "pTime" },
        { label: "장비 Part No.", key: "pDevice" },
        { label: "암모니아 ppm", key: "pNH3" },
        { label: "황화수소 ppm", key: "pH2S" },
        { label: "온도 ℃", key: "pTemperature" },
        { label: "습도 %", key: "pHumidity" },
        { label: "신호세기 dBm", key: "pRssi" },
        { label: "신호품질 dB", key: "pSnr" }
    ];

    const [sortDirection, setSortDirection] = useState('desc'); // 초기값: 내림차순 (최신순)

    const toggleSortDirection = () => {
        setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    };
    
    // 정렬 기준
    const sortedPosts = useMemo(() => {
        return [...posts].sort((a, b) => {
            return sortDirection === 'asc'
                ? a.pTimestamp - b.pTimestamp
                : b.pTimestamp - a.pTimestamp;
        });
    }, [posts, sortDirection]);
    
    // const pagedData = posts.slice(dataCount * (page - 1), dataCount * page);
    const POST_PER_PAGE = 10; // 또는 원하는 숫자
    const pagedData = sortedPosts.slice((page - 1) * POST_PER_PAGE, page * POST_PER_PAGE);

    return (
        <div className="container">
            <div className="header-wrap">
                <h1>Home</h1>
                <div className="info-wrap">
                    <div className="info-id">
                        <span>{userAccount.company}</span>
                        <button className="logout-btn" onClick={onLogoutHandler}>로그아웃</button>
                    </div>
                    <div className="info-email">
                        {userAccount.email}
                    </div>
                </div>
            </div>

            <div className="bodyHomePrintList">
                <div className="HomePrintListLayout">
                    <div className="setting-layout-header">
                        <h3>기간별 측정 이력</h3> <span>Measurement history by period</span>
                    </div>
                    <div className="HomePrintListLayoutBody">
                        <div className="HomePrintListSelect">
                            <Box sx={{ minWidth: 200 }}>
                                <FormControl fullWidth>
                                    <InputLabel id="device-select-label">장비 선택</InputLabel>
                                    <Select
                                        labelId="device-select-label"
                                        value={PartNo}
                                        label="PartNo"
                                        onChange={onChangeHandler}
                                        style={{ borderRadius: '8px' }}
                                    >
                                        <MenuItem value={'ALL'}>전체</MenuItem>
                                        {deviceListInfo.map((deviceInfo) => (
                                            <MenuItem key={deviceInfo.sensorNumber} value={deviceInfo.sensorNumber}>
                                                {deviceInfo.sensorNumber}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            <div className="CalendarSearch">
                                <label className="CalendarLabel">기간 검색</label>
                                <div className="CalendarGroup">
                                    <DatePicker
                                        selected={startDate}
                                        onChange={setStartDate}
                                        dateFormat="yyyy-MM-dd"
                                        className="DPstartDate"
                                    />
                                    <span className="CalendarTilde">~</span>
                                    <DatePicker
                                        selected={endDate}
                                        onChange={setEndDate}
                                        dateFormat="yyyy-MM-dd"
                                        className="DPstartDate"
                                        minDate={startDate}
                                    />
                                </div>
                            </div>

                            <Button
                                variant="contained"
                                onClick={handleSearch}
                                style={{ backgroundColor: '#4e82eb', borderRadius: '8px', padding: '6px 32px', color: 'white' }}
                            >
                                검 색
                            </Button>
                        </div>

                        <div className="HomePrintListTable">
                            <ul className="realtimeTableTh">
                                <li onClick={toggleSortDirection} style={{ cursor: 'pointer' }}>
                                    측정시간 {sortDirection === 'asc' ? '▲' : '▼'}
                                </li>
                                <li>장비 Part No.</li>
                                <li>암모니아 ppm</li>
                                <li>황화수소 ppm</li>
                                <li>온도 ℃</li>
                                <li>습도 %</li>
                                <li>신호세기 dBm</li>
                                <li>신호품질 dB</li>
                            </ul>
                            {loading ? (
                                <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>
                                ) : (
                                pagedData.length > 0 ? (
                                    pagedData.map((el) => (
                                    <ul className="realtimeTableTd" key={el.pID}>
                                        <li>{el.pTime}</li>
                                        <li>{el.pDevice}</li>
                                        <li>
                                            {el.pNH3 != null
                                                ? parseFloat(el.pNH3).toFixed(2)
                                                : '0.00'}
                                        </li>
                                        <li>
                                            {el.pH2S != null
                                                ? parseFloat(el.pH2S).toFixed(2)
                                                : '0.00'}
                                        </li>
                                        <li>
                                            {el.pTemperature != null
                                                ? parseFloat(el.pTemperature).toFixed(2)
                                                : '0.00'}
                                        </li>
                                        <li>
                                            {el.pHumidity != null
                                                ? parseFloat(el.pHumidity).toFixed(3)
                                                : '0.000'}
                                        </li>
                                        <li>
                                            {el.pRssi != null
                                                ? el.pRssi
                                                : '0'}
                                        </li>
                                        <li>
                                            {el.pSnr != null
                                                ? parseFloat(el.pSnr).toFixed(1)
                                                : '0.0'}
                                        </li>
                                    </ul>
                                    ))
                                ) : (
                                    <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                                    검색된 측정 이력이 없습니다.
                                    </div>
                                )
                            )}
                        </div>

                        <div className="HomePrintListBtn">
                            <CSVLink
                                headers={headers}
                                data={arrayPostsRef.current}
                                target="_blank"
                                filename={`측정이력_${moment().format('YYYYMMDD')}_${moment().format('HHmmss')}`}
                            >
                                <Button
                                    style={{ backgroundColor: '#4e82eb', borderRadius: '8px', padding: '6px 32px', color: 'white' }}
                                    variant="outlined"
                                    disabled={posts.length === 0}
                                >
                                    측정이력 저장
                                </Button>
                            </CSVLink>
                        </div>

                        {LAST_PAGE > 1 && (
                            <div className="pagination">
                                <Pagination
                                    shape="rounded"
                                    count={LAST_PAGE}
                                    page={page}
                                    onChange={onPageHandler}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default HomePrintList;
